import React, { useState, useEffect } from 'react';
import { Modal, Button, Container, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import RepairServies from '../../../services/RepairServices';
import PoServices from "../../../services/PoServices";
import Select from "react-select";

const AddMrvFormLocal = ({ show, handleClose, store, onSuccess }) => {

  const initialFormValues = {
    store: '',
    supplier: '',
    billingNo: '',
    billingDate: '',
    image: null,
    itemCount: '',
    otherCharges: '',
    totalAmount: '',
  };

  const [formValues, setFormValues] = useState(initialFormValues);
  const [previewImage, setPreviewImage] = useState('img/placeholder-img.png');
  const [suppliers, setSuppliers] = useState([]);

  // --------------------------------------------------
  // AUTO SELECT STORE (IF ONLY ONE)
  // --------------------------------------------------
  useEffect(() => {
    if (show && store?.length === 1) {
      setFormValues(prev => ({
        ...prev,
        store: store[0]._id
      }));
    }
  }, [show, store]);

  // --------------------------------------------------
  // FETCH LOCAL SUPPLIERS
  // --------------------------------------------------
  useEffect(() => {
    if (show) {
      PoServices.activeSuppliers()
        .then((res) => {
          const allSuppliers = res.data || [];
          const localSuppliers = allSuppliers.filter(s =>
            s.name?.toLowerCase().startsWith("local")
          );
          setSuppliers(localSuppliers);
        })
        .catch(() => {
          Swal.fire("Error", "Failed to fetch suppliers", "error");
        });
    }
  }, [show]);

  // --------------------------------------------------
  // INPUT HANDLER
  // --------------------------------------------------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // --------------------------------------------------
  // FILE HANDLER
  // --------------------------------------------------
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setPreviewImage(reader.result);
    reader.readAsDataURL(file);

    setFormValues(prev => ({
      ...prev,
      image: file
    }));
  };

  // --------------------------------------------------
  // VALIDATION
  // --------------------------------------------------
  const validateForm = () => {
    if (!formValues.store) {
      Swal.fire("Warning", "Please select a store", "warning");
      return false;
    }
    if (!formValues.supplier) {
      Swal.fire("Warning", "Please select a supplier", "warning");
      return false;
    }
    if (!formValues.itemCount || formValues.itemCount <= 0) {
      Swal.fire("Warning", "Enter valid item count", "warning");
      return false;
    }
    if (formValues.otherCharges === '') {
      Swal.fire("Warning", "Please enter other charges", "warning");
      return false;
    }
    if (!formValues.totalAmount || formValues.totalAmount <= 0) {
      Swal.fire("Warning", "Please enter total amount", "warning");
      return false;
    }
    return true;
  };

  // --------------------------------------------------
  // SUBMIT
  // --------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const formData = new FormData();

      Object.entries(formValues).forEach(([key, value]) => {
        if (key === 'image') {
          if (value) formData.append('image', value);
        } else if (key === 'otherCharges' || key === 'totalAmount') {
          formData.append(key, Number(value));
        } else {
          formData.append(key, value);
        }
      });

      await RepairServies.CreateMrv(formData);

      Swal.fire("Success", "MRV created successfully", "success");
      handleClose();
      onSuccess?.();

    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Failed to create MRV",
        "error"
      );
    }
  };

  // --------------------------------------------------
  // RESET ON CLOSE
  // --------------------------------------------------
  useEffect(() => {
    if (!show) {
      setFormValues(initialFormValues);
      setPreviewImage('img/placeholder-img.png');
    }
  }, [show]);

  return (
    <Modal show={show} onHide={handleClose}>
      <form onSubmit={handleSubmit}>
        <Modal.Header>
          <Modal.Title>
            <i className="fas fa-edit"></i> MRV Form (Local)
          </Modal.Title>
          <Button className="btn-close" onClick={handleClose}></Button>
        </Modal.Header>

        <Modal.Body>
          <Container>
            <Row>

              {/* Store Dropdown */}
              <Col md="12">
                <label>Store</label>

                <Select
                  options={store.map((s) => ({
                    value: s._id,
                    label: s.name,
                  }))}
                  value={
                    store
                      .map((s) => ({
                        value: s._id,
                        label: s.name,
                      }))
                      .find((opt) => opt.value === formValues.store) || null
                  }
                  onChange={(selected) =>
                    setFormValues({
                      ...formValues,
                      store: selected ? selected.value : "",
                    })
                  }
                  placeholder="Search Store..."
                  noOptionsMessage={() => "No stores found"}
                  isSearchable
                />
              </Col>

              {/* Supplier Dropdown */}
              <Col md="12" className="mt-3">
                <label>Supplier</label>

                <Select
                  options={suppliers.map((sup) => ({
                    value: sup._id,
                    label: `${sup?.name} - ${sup?.address?.state || ""}`,
                  }))}
                  value={
                    suppliers
                      .map((sup) => ({
                        value: sup._id,
                        label: `${sup?.name} - ${sup?.address?.state || ""}`,
                      }))
                      .find((opt) => opt.value === formValues.supplier) || null
                  }
                  onChange={(selected) =>
                    setFormValues({
                      ...formValues,
                      supplier: selected ? selected.value : "",
                    })
                  }
                  placeholder="Search Supplier..."
                  noOptionsMessage={() => "No suppliers found"}
                  isSearchable
                />
              </Col>

              <Col md="6 mt-3">
                <label>Bill Number</label>
                <input
                  type="number"
                  name="billingNo"
                  value={formValues.billingNo}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              <Col md="6 mt-3">
                <label>Bill Date</label>
                <input
                  type="date"
                  name="billingDate"
                  value={formValues.billingDate}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              <Col md="6 mt-3">
                <label>Item Count</label>
                <input
                  type="number"
                  name="itemCount"
                  value={formValues.itemCount}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              <Col md="6 mt-3">
                <label>Other Charges</label>
                <input
                  type="number"
                  name="otherCharges"
                  value={formValues.otherCharges}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              <Col md="12 mt-3">
                <label>Total Amount</label>
                <input
                  type="number"
                  name="totalAmount"
                  value={formValues.totalAmount}
                  onChange={handleInputChange}
                  className="form-control form-control-sm"
                />
              </Col>

              <Col md="12 mt-3">
                <label>Upload Bill Image</label>
                <input
                  type="file"
                  Pending-PO-List
                  accept="image/*"
                  capture="environment"
                  className="form-control form-control-sm"
                  onChange={handleFileChange}
                />
                {previewImage !== 'img/placeholder-img.png' && (
                  <img src={previewImage} alt="Preview" className="img-fluid mt-2" />
                )}
              </Col>

            </Row>
          </Container>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="danger light" onClick={handleClose}>Close</Button>
          <Button variant="primary" type="submit">Save</Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AddMrvFormLocal;
